import { sanitizeMotionScene } from '@/lib/motion/sanitizeMotionScene';
import { patchMotionScene } from '@/lib/motion/patchMotionScene';
import { motionReference } from '@/lib/motion/reference';
import { motionCaptionCues } from '@/lib/motion/text';
import type { MotionElement, MotionPatch, MotionScene } from '@/lib/motion/types';
import type { MotionLayer, LayerRect } from '@/lib/layers/types';
import type { EditorDraft, EditorEnv } from './applyEditorCalls';
import { clipDurationOf } from './sceneEdits';

export class MotionToolError extends Error {
  constructor(readonly code: string, readonly detail?: string) { super(code); }
}

type Args = Record<string, unknown>;
const fail = (code: string, detail?: string): never => { throw new MotionToolError(code, detail); };
const finite = (value: unknown, fallback: number) => {
  if (value === undefined) return fallback;
  if (typeof value !== 'number' || !Number.isFinite(value)) return fail('invalid_number');
  return value;
};

function span(args: Args, duration: number, current?: MotionLayer) {
  const start = finite(args.start, current?.start ?? 0);
  const end = Math.min(duration, finite(args.end, current?.end ?? start + 5));
  if (start < 0 || start >= duration || end - start < 1 / 30 - 1e-8) fail('outside_clip');
  return { start, end };
}

function rectOf(raw: unknown, fallback: LayerRect = { x: 0, y: 0, w: 1, h: 1 }): LayerRect {
  if (raw === undefined) return { ...fallback };
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return fail('invalid_rect');
  const item = raw as Record<string, unknown>;
  const result = { x: finite(item.x, fallback.x), y: finite(item.y, fallback.y), w: finite(item.w, fallback.w), h: finite(item.h, fallback.h) };
  if (Math.abs(result.x) > 10 || Math.abs(result.y) > 10 || result.w <= 0 || result.h <= 0 || result.w > 20 || result.h > 20) fail('invalid_rect');
  return result;
}

/** A motion layer scales the whole design canvas into its rect, so a rect
 * whose w differs from h would stretch it. Fit the largest frame-shaped box,
 * centered, inside the rect the model asked for. */
function frameShapedRect(rect: LayerRect): LayerRect {
  const side = Math.min(rect.w, rect.h);
  const round = (value: number) => Math.round(value * 1e6) / 1e6;
  return { x: round(rect.x + (rect.w - side) / 2), y: round(rect.y + (rect.h - side) / 2), w: round(side), h: round(side) };
}

const named = (value: string) => value.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

/** Resolve catalog fonts and search-result media before validating. Existing
 * assets stay usable when another call patches their parent element. */
function resolveAssets(raw: unknown, env: EditorEnv, existing?: MotionScene): unknown {
  let encoded: string;
  try { encoded = JSON.stringify(raw); } catch { return fail('invalid_motion_scene'); }
  if (!encoded || new TextEncoder().encode(encoded).length > 65536) return fail('invalid_motion_scene', 'scene_too_large');
  const scene = JSON.parse(encoded);
  const knownUrls = new Set([...env.media.values()].filter((item) => item.kind !== 'audio').map((item) => item.url));
  const knownFonts = new Map<string, string | undefined>();
  const collect = (elements: readonly MotionElement[]) => elements.forEach((element) => {
    if (element.src) knownUrls.add(element.src);
    if (element.font) knownFonts.set(element.font, element.fontUrl);
    element.tiers?.forEach((tier) => { if (tier.font) knownFonts.set(tier.font, tier.fontUrl); });
    if (element.children) collect(element.children);
  });
  const saved = existing ? sanitizeMotionScene(existing).scene : null;
  if (saved) collect(saved.elements);
  const resolveFont = (value: Record<string, unknown>) => {
    if (value.font === undefined) { delete value.fontUrl; return; }
    if (typeof value.font !== 'string') return fail('unknown_font');
    const requested = value.font;
    const font = env.fonts.find((entry) => entry.id === requested || named(entry.name) === named(requested));
    if (font) {
      value.font = font.id;
      if (font.fileUrl) value.fontUrl = font.fileUrl; else delete value.fontUrl;
    } else if (knownFonts.has(requested)) {
      const url = knownFonts.get(requested);
      if (url) value.fontUrl = url; else delete value.fontUrl;
    } else fail('unknown_font', 'Use options.fonts');
  };
  const visit = (elements: unknown, depth = 0) => {
    if (!Array.isArray(elements)) return;
    if (depth > 12) return fail('invalid_motion_scene', 'nesting_too_deep');
    elements.forEach((element: Record<string, unknown>) => {
      if (!element || typeof element !== 'object' || Array.isArray(element)) return;
      if (element.type === 'text' || element.type === 'captions') resolveFont(element);
      if (Array.isArray(element.tiers)) element.tiers.forEach((tier) => { if (tier && typeof tier === 'object') resolveFont(tier); });
      if (element.src !== undefined) {
        const item = env.media.get(String(element.src));
        if (item && item.kind !== 'audio') element.src = item.url;
        else if (!knownUrls.has(String(element.src))) return fail('unknown_media', 'Use an item_id from editor_search_media as src');
      }
      visit(element.children, depth + 1);
    });
  };
  visit(scene?.elements);
  return scene;
}

function validated(raw: unknown, env: EditorEnv, existing?: MotionScene): MotionScene {
  const result = sanitizeMotionScene(resolveAssets(raw, env, existing));
  if (!result.scene) return fail('invalid_motion_scene', result.issues.filter((issue) => issue.severity === 'error').slice(0, 4).map((issue) => `${issue.path}: ${issue.code}`).join('; '));
  return result.scene;
}

/** Never let the backend silently truncate a scene. Large scenes are returned
 * as explicit JSON string chunks that can be reassembled by cursor. */
function readScene(layer: MotionLayer, rawCursor: unknown) {
  const json = JSON.stringify(layer.scene);
  if (json.length < 4800 && rawCursor === undefined) return { id: layer.id, start: layer.start, end: layer.end, rect: layer.rect, scene: layer.scene };
  const cursor = finite(rawCursor, 0);
  if (!Number.isInteger(cursor) || cursor < 0 || cursor > json.length) return fail('invalid_cursor');
  let length = Math.min(4000, json.length - cursor);
  const make = () => ({ id: layer.id, format: 'json_chunks', scene_json: json.slice(cursor, cursor + length), cursor, next_cursor: cursor + length < json.length ? cursor + length : null, total_chars: json.length });
  while (JSON.stringify(make()).length > 5500 && length > 0) length -= 100;
  return make();
}

function captionScene(draft: EditorDraft, args: Args, env: EditorEnv, window: { start: number; end: number }): MotionScene {
  if (!Array.isArray(args.tiers) || !args.tiers.length || args.tiers.length > 6) return fail('invalid_motion_scene', 'tiers: provide 1–6 styles');
  // Tier fields are camelCase; accept the snake_case of the top-level args too.
  const tiers = (args.tiers as Array<Record<string, unknown>>).map((tier) => {
    if (!tier || typeof tier !== 'object') return tier;
    const { word_enter: wordEnter, active_word: activeWord, spoken_word: spokenWord, ...rest } = tier;
    return {
      ...rest,
      ...(wordEnter !== undefined && rest.wordEnter === undefined ? { wordEnter } : {}),
      ...(activeWord !== undefined && rest.activeWord === undefined ? { activeWord } : {}),
      ...(spokenWord !== undefined && rest.spokenWord === undefined ? { spokenWord } : {}),
    };
  });
  const ids = tiers.map((tier) => tier?.id);
  if (ids.some((id) => typeof id !== 'string' || !id)) return fail('invalid_motion_scene', 'Each tier needs an id');
  if (tiers.some((tier) => tier.lineHeight !== undefined
    && (typeof tier.lineHeight !== 'number' || !Number.isFinite(tier.lineHeight)
      || tier.lineHeight < 0.5 || tier.lineHeight > 3))) {
    return fail('invalid_number', 'tiers.lineHeight: use a unitless font-size multiplier from 0.5 to 3 (usually 1.15), not pixels');
  }
  const wordsPerCue = finite(args.words_per_cue, 6);
  if (!Number.isInteger(wordsPerCue) || wordsPerCue < 1 || wordsPerCue > 12) return fail('invalid_number', 'words_per_cue: 1–12');
  const pattern = args.tier_pattern === undefined ? [] : args.tier_pattern;
  if (!Array.isArray(pattern) || pattern.length > 12 || pattern.some((id) => !ids.includes(id))) return fail('invalid_option', 'tier_pattern: use tier ids');
  const duration = window.end - window.start;
  const rounded = (time: number) => Math.round(time * 1e6) / 1e6;
  const timed: Array<{ text: string; start: number; end: number }> = [];
  let offset = 0;
  const sourceWords = [...(env.words ?? [])].sort((a, b) => a.start - b.start);
  draft.scenes.forEach((scene) => {
    sourceWords.forEach((word) => {
      if (!Number.isFinite(word.start) || !Number.isFinite(word.end) || word.end <= word.start) return;
      if (word.start < scene.startTime || word.start >= scene.endTime) return;
      const start = offset + word.start - scene.startTime;
      const end = offset + Math.min(word.end, scene.endTime) - scene.startTime;
      const text = (word.punctuated_word || word.word || '').trim();
      if (!text || start >= window.end || end <= window.start) return;
      // Rounding must not push a word that runs to the cut past the element
      // end, nor leave one with no length there.
      const at = rounded(Math.max(start, window.start) - window.start);
      const until = Math.min(duration, rounded(Math.min(end, window.end) - window.start));
      if (until > at) timed.push({ text, start: at, end: until });
    });
    offset += scene.endTime - scene.startTime;
  });
  if (!timed.length) return fail('no_transcript');
  // Tiers follow each word's position in the cue the renderer will show, so a
  // pause (which starts a new cue) restarts the pattern instead of shifting it.
  // Without a pattern, tiers spread over the cue's actual length (a short
  // cue still reaches the lower tiers).
  const words = motionCaptionCues(timed, wordsPerCue).flatMap(({ first, last }) => timed.slice(first, last).map((word, index) => ({
    ...word,
    tier: String(pattern.length ? pattern[index % pattern.length] : ids[Math.min(ids.length - 1, Math.floor(index * ids.length / (last - first)))]),
  })));
  const height = env.compAspect < 1 ? 1080 / env.compAspect : 1080;
  return validated({ version: 1, elements: [{
    id: 'captions', type: 'captions', start: 0, end: duration,
    x: finite(args.x, 80), y: finite(args.y, height * 0.58),
    maxWidth: finite(args.max_width, (env.compAspect > 1 ? 1080 * env.compAspect : 1080) - 160),
    wordsPerCue, words, tiers,
    ...(args.cue_enter !== undefined ? { cueEnter: args.cue_enter } : {}),
    ...(args.cue_exit !== undefined ? { cueExit: args.cue_exit } : {}),
    ...(args.word_enter !== undefined ? { wordEnter: args.word_enter } : {}),
    ...(args.active_word !== undefined ? { activeWord: args.active_word } : {}),
    ...(args.spoken_word !== undefined ? { spokenWord: args.spoken_word } : {}),
  }] }, env);
}

export function applyMotionTool(draft: EditorDraft, name: string, args: Args, env: EditorEnv): { draft: EditorDraft; result: unknown; changed: boolean; subtitleChanged?: boolean } {
  if (name === 'editor_motion_reference') return { draft, result: { reference: motionReference(typeof args.topic === 'string' ? args.topic : undefined) }, changed: false };
  if (name === 'editor_add_motion' || name === 'editor_add_captions') {
    if (draft.layers.filter((layer) => layer.type === 'motion').length >= 20) return fail('too_many_motion_layers');
    const duration = clipDurationOf(draft.scenes);
    const window = span(name === 'editor_add_captions' ? { end: duration, ...args } : args, duration);
    const scene = name === 'editor_add_captions' ? captionScene(draft, args, env, window) : validated(args.scene, env);
    const layer: MotionLayer = {
      type: 'motion', id: `layer-ai-${env.newId()}`, name: typeof args.name === 'string' ? args.name.slice(0, 120) : (name === 'editor_add_captions' ? 'Captions' : 'Animation'),
      ...window, rect: frameShapedRect(rectOf(args.rect)), rotation: 0, opacity: 1, scene,
    };
    if (args.disable_subtitles !== undefined && typeof args.disable_subtitles !== 'boolean') return fail('invalid_boolean');
    const subtitleChanged = name === 'editor_add_captions' && args.disable_subtitles === true && draft.subtitlesEnabled;
    return { draft: { ...draft, layers: [...draft.layers, layer], ...(subtitleChanged ? { subtitlesEnabled: false } : {}) }, changed: true, subtitleChanged,
      result: { id: layer.id, start: layer.start, end: layer.end, rect: layer.rect, elements: scene.elements.map((element) => ({ id: element.id, type: element.type })) } };
  }
  const layer = draft.layers.find((item) => item.id === args.id);
  if (!layer) return fail('layer_not_found');
  if (layer.type !== 'motion') return fail('not_a_motion_layer');
  if (name === 'editor_get_motion') return { draft, result: readScene(layer, args.cursor), changed: false };
  if (args.scene !== undefined && args.patch !== undefined) return fail('invalid_arguments', 'Pass scene OR patch');
  let scene = layer.scene;
  if (args.scene !== undefined) scene = validated(args.scene, env, layer.scene);
  if (args.patch !== undefined) {
    if (!Array.isArray(args.patch) || args.patch.length > 100) return fail('invalid_arguments');
    const patches = (args.patch as MotionPatch[]).map((patch) => {
      if (patch?.path === 'src' && patch.op === 'set') {
        const item = env.media.get(String(patch.value));
        if (item && item.kind !== 'audio') return { ...patch, value: item.url };
      }
      if (Array.isArray(patch?.value) && /(?:^|\.)children$/.test(patch.path)) {
        const resolved = resolveAssets({ version: 1, elements: patch.value }, env, layer.scene) as MotionScene;
        return { ...patch, value: resolved.elements };
      }
      if (patch?.value && typeof patch.value === 'object' && !Array.isArray(patch.value) && 'type' in patch.value) {
        const resolved = resolveAssets({ version: 1, elements: [patch.value] }, env, layer.scene) as MotionScene;
        return { ...patch, value: resolved.elements[0] };
      }
      return patch;
    });
    const patched = patchMotionScene(scene, patches);
    if (!patched.scene) return fail('invalid_motion_scene', patched.issues.filter((issue) => issue.severity === 'error').slice(0, 4).map((issue) => `${issue.path}: ${issue.code}`).join('; '));
    scene = validated(patched.scene, env, layer.scene);
  }
  if (['scene', 'patch', 'start', 'end', 'rect'].every((key) => args[key] === undefined)) return fail('nothing_to_change');
  const next: MotionLayer = { ...layer, ...span(args, clipDurationOf(draft.scenes), layer), rect: args.rect === undefined ? layer.rect : frameShapedRect(rectOf(args.rect, layer.rect)), scene };
  return { draft: { ...draft, layers: draft.layers.map((item) => item.id === layer.id ? next : item) }, changed: true,
    result: { id: layer.id, start: next.start, end: next.end, rect: next.rect } };
}
